/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useForm, useGeolocation } from '@hooks';
import { renderSomethingWentWrong } from '@utils';
import { useErrorReporter } from '@lib/errorReporter';
import { useAuth } from '@lib/authentication';
import { useConnectionChange } from '@lib/connectionChange';
import { useInsightsService } from '@lib/insightsService';
import { useProfile } from '@lib/profile';
import { VPN_OR_PROXY_LOGIN_ERROR } from '@constants';
import {
  AuthenticationAttemptsLimitReached,
  DistanceExceededFromLastConnection,
  InvalidCredentials,
  InvestorHasAlreadyAnActiveSession,
  UnusualAccess
} from '@lib/hundredBricksAPI/authentication';

import ActionText from '../ActionText';
import Button from '../Button';
import Form from '../Form';
import Modal from '../Modal';
import PasswordField from '../PasswordField';
import toast from '../toast';

import RenewSessionMessage from './RenewSessionMessage';
import RenewSessionOptions from './RenewSessionOptions';
import {
  ModalTitle,
  ButtonContainer,
  LinksContainer,
  VPNErrorContainer,
  OptionContainer,
  ModalMessageContainer
} from './RenewSessionModal.style';

const passwordSchema = {
  password: {
    required: true
  }
};

const ERROR_INCORRECT_PASSWORD = 'La contraseña es inválida.';
const ERROR_LOCATION_BLOCKED = 'Ubicación desactivada. Activa los permisos de ubicación '
  + 'en tu navegador para acceder a tu cuenta.';

const RenewSessionModal = ({
  isOpen,
  onClose,
  title,
  description
}) => {
  const navigate = useNavigate();
  const form = useForm({ initialValues: { password: '' }, schema: passwordSchema });
  const errorReporter = useErrorReporter();
  const { authenticationEvent, reportInsight } = useInsightsService();
  const { getCurrentPosition } = useGeolocation();
  const { profile, isLoading: isLoadingProfile } = useProfile();
  const { hasConnectionChangeError, setConnectionChangeData } = useConnectionChange();

  const {
    renewSession,
    error: loginError,
    isLoading: isAuthenticating,
    logout,
    clearSession,
    token
  } = useAuth();

  const email = profile.email.address;
  const tokenValue = useRef(token?.value).current;

  const onSubmit = (latitude, longitude) => {
    renewSession(email, form.values.password, latitude, longitude);
  };

  const handleSubmit = async () => {
    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      onSubmit(latitude, longitude);
    }, () => {
      toast.danger(ERROR_LOCATION_BLOCKED);
    });
  };

  const handleForgotPassword = () => {
    clearSession();
    navigate('/forgot-password');
  };

  const handleUnusualAccessUnlock = () => {
    clearSession();
    navigate(`/unusual-access-unblock?email=${encodeURIComponent(email)}`);
  };

  const handleLoginError = error => {
    reportInsight(authenticationEvent.logInError, error);

    if (error instanceof InvalidCredentials) {
      form.addError({
        field: 'password',
        message: ERROR_INCORRECT_PASSWORD
      });
    } else {
      errorReporter.critical(error);
      renderSomethingWentWrong();
    }
  };

  const hasModalError = loginError instanceof AuthenticationAttemptsLimitReached
    || loginError instanceof InvestorHasAlreadyAnActiveSession
    || loginError instanceof UnusualAccess;

  useEffect(() => {
    if (token && tokenValue !== token.value) {
      onClose();
      form.resetValues();

      if (hasConnectionChangeError) {
        setConnectionChangeData({ error: undefined, hasConnectionChangeError: false });
      }
    }
  }, [token]);

  useEffect(() => {
    if (loginError && !hasModalError) {
      handleLoginError(loginError);
    }
  }, [loginError]);

  return (
    <Modal isOpen={isOpen} preventClose>
      <ModalTitle>{title}</ModalTitle>

      {hasModalError
        ? (
          <ModalMessageContainer>
            <RenewSessionMessage error={loginError} />

            <OptionContainer>
              <RenewSessionOptions
                error={loginError}
                onConfirm={handleUnusualAccessUnlock}
                onCancel={logout}
              />
            </OptionContainer>
          </ModalMessageContainer>
        )
        : (
          <div>
            <Form onSubmit={form.handleSubmit(handleSubmit)}>
              <PasswordField
                id="password"
                focus
                label={description}
                placeholder="Escribe tu contraseña"
                value={form.values.password}
                {...form.fieldProps('password')}
              />

              {
                loginError instanceof DistanceExceededFromLastConnection && (
                  <VPNErrorContainer>
                    {VPN_OR_PROXY_LOGIN_ERROR}
                  </VPNErrorContainer>
                )
              }

              <ButtonContainer>
                <Button
                  type="submit"
                  isLoading={isAuthenticating || isLoadingProfile}
                >
                  Reactivar sesión
                </Button>
              </ButtonContainer>
            </Form>

            <LinksContainer>
              <ActionText
                color="info"
                onClick={handleForgotPassword}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Restablece tu contraseña
              </ActionText>

              <ActionText
                color="info"
                onClick={logout}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Cerrar sesión
              </ActionText>
            </LinksContainer>
          </div>
        )}
    </Modal>
  );
};

RenewSessionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

export default RenewSessionModal;
